/**
 * Create a deterministic hex string from a random input string
 */
export async function stringToHexColor(inputString: string) {
  const encoder = new TextEncoder()
  const data = encoder.encode(inputString)
  const hashBuffer = await crypto.subtle.digest('SHA-256', data)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
  return `#${hashHex.slice(0, 6)}`
}

/**
 * Extract opacity from hex color value
 * @param hexColor - Color in hex format (#RRGGBB or #RRGGBBAA)
 * @returns Opacity value between 0 and 1
 */
export function extractOpacityFromHex(hexColor: string): number {
  // Remove # if present
  const hex = hexColor.replace('#', '')

  // Check if alpha channel is present (8 characters)
  if (hex.length === 8) {
    // Extract alpha channel (last 2 characters)
    const alpha = hex.substring(6, 8)
    // Convert from hex (00-FF) to decimal (0-1)
    return parseInt(alpha, 16) / 255
  }

  // No alpha channel, return full opacity
  return 1
}

/**
 * Takes hex color and returns it with given opacity
 */
export function hexWithOpacity(hex: `#${string}`, opacity: number): string {
  // Remove leading #
  let cleanHex = hex.replace('#', '')

  // Handle shorthand hex (#RGB)
  if (cleanHex.length === 3) {
    cleanHex = cleanHex
      .split('')
      .map((ch) => ch + ch)
      .join('')
  }

  if (cleanHex.length !== 6) {
    throw new Error('Ungültiger Hex-Farbwert.')
  }

  // Clamp opacity between 0 and 1
  if (opacity < 0) opacity = 0
  if (opacity > 1) opacity = 1

  // Compute alpha as integer (0–255)
  const alpha = Math.round(opacity * 255)

  // Convert to two-digit hex
  const alphaHex = alpha.toString(16).padStart(2, '0').toUpperCase()

  return `#${cleanHex.toUpperCase()}${alphaHex}`
}

/**
 * Create a deterministic hex string from a random input string
 */
export function createNumberToHexColorFn() {
  // Rotate/Multiply by different values to get better range of colors
  const RotateColors = [50, -300, 500]
  let pointer = 0

  function numberToHexColor(number: number) {
    if (RotateColors[pointer] === undefined) {
      pointer = 0
    }
    const rotate = RotateColors[pointer]
    pointer++

    const inputString = (number * rotate).toString()
    let hash = 0
    for (let i = 0; i < inputString.length; i++) {
      const char = inputString.charCodeAt(i)
      hash = (hash << 5) - hash + char
      hash = hash & hash // Convert to 32-bit integer
    }

    // Convert to 6-digit hex color
    const color = (hash & 0xffffff).toString(16).toUpperCase()

    // Pad with zeros if necessary and add '#' prefix
    return '#' + '000000'.substring(0, 6 - color.length) + color
  }

  return numberToHexColor
}

/**
 * https://stackoverflow.com/questions/5623838/rgb-to-hex-and-hex-to-rgb
 */
export function hex2rgb(hex: string) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null
}
